import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  InvalidateQueryFilters,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useUnmount } from "react-use";

import {
  getSearchError,
  SAMPLES_REFETCH_INTERVAL,
  showCancelRequestNotification,
  useQueryValidationContext
} from "./index";
import { LOCATION } from "../consts";
import {
  constructNetworkSelections,
  extractNetworkValuesFromQuery,
  getNonNTIKeywordsFromQuery,
  showCloudUnreachableOrNoAccessNotifications
} from "../util";
import {
  filterByClassification,
  filterByDate,
  filterByResourceType,
  formatNetworkSelections,
  isNetworkOnlySearch,
  sortNetworkReferences
} from "../components/network/utils";
import api from "../api";
import { advancedSearchKeys } from "../../samples/queryKeys";
import { STATUS_CODES } from "../../../enums";
import {
  ErrorResponse,
  ValidationErrorResponse
} from "../../../requests/@types";
import { NetworkSearchResponse } from "../../../services/@types";
import useSearchContext from "./useSearchContext";
import { useReducedSystemHeathStatus } from "../../header/system-health/hooks/useSystemHealthStatus";
import { AnalysisStatus } from "../../../lib/enums";

const FETCH_URLS_IN_ANALYZING_STATE_REFETCH_INTERVAL = 5000;

const useNetworkSearch = () => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const cachedNetworkRefs = useRef<string[] | undefined>(undefined);

  useUnmount(() => {
    abortControllerRef.current?.abort();
  });

  const queryClient = useQueryClient();

  const { location: params, navigate } = useSearchContext();

  const {
    data: validationData,
    isLoading: isValidating,
    error: validationError
  } = useQueryValidationContext();

  const { isCloudDisabled } = useReducedSystemHeathStatus();

  const { q, page, pagesize, selections, sort, location } = params;

  const isNetwork = location === LOCATION.NETWORK;

  const formattedSelections = useMemo(
    () => formatNetworkSelections(q, selections, isNetwork),
    [q, selections, isNetwork]
  );
  const {
    classification: { value: classification = "" } = {},
    network_resource: { value: type = "" } = {},
    time_type: { value: timeType = "" } = {},
    period_type: periodType,
    "submission-user": { value: submissionUser = "" } = {}
  } = formattedSelections ?? {};

  const { networkValues, tooLongNetworkValues } = useMemo(
    () => extractNetworkValuesFromQuery(q ?? ""),
    [q]
  );
  const nonNTIKeywords = useMemo(
    () => getNonNTIKeywordsFromQuery(q ?? ""),
    [q]
  );
  const isNetworkOnly = isNetworkOnlySearch(q ?? "", networkValues);
  const pageSize = isNetwork ? pagesize : 0;
  const userIsSearching = Boolean(networkValues.length);
  const returnNoData = !!q && !userIsSearching && !!nonNTIKeywords.length;
  useEffect(() => {
    if (isNetworkOnly && location !== LOCATION.NETWORK)
      navigate({ location: LOCATION.NETWORK });
  }, [location, isNetworkOnly, navigate]);

  useEffect(() => {
    cachedNetworkRefs.current = undefined;
  }, [periodType, timeType]);

  const networkQueryKey = useMemo(() => {
    if (userIsSearching) {
      return advancedSearchKeys.fetchNetworkDataKey(q);
    } else {
      return advancedSearchKeys.fetchNetworkDataKey(
        q,
        periodType,
        timeType,
        submissionUser
      );
    }
  }, [periodType, q, timeType, userIsSearching, submissionUser]);

  const search = async () => {
    abortControllerRef.current?.abort("Aborting previous call.");
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    return api
      .retrieveNetworkSearchData(
        {
          network_references: networkValues,
          cached_references: userIsSearching ? [] : cachedNetworkRefs.current,
          ...constructNetworkSelections(formattedSelections)
        },
        abortController.signal
      )
      .then((r: { data: NetworkSearchResponse }) => {
        cachedNetworkRefs.current = r.data?.cached_references;
        return r.data;
      });
  };

  const {
    data: networkData,
    error: networkError,
    isError,
    isLoading
  } = useQuery<NetworkSearchResponse, ErrorResponse<ValidationErrorResponse>>({
    queryKey: networkQueryKey,
    queryFn: search,
    enabled:
      Boolean(validationData?.is_valid) &&
      !isCloudDisabled &&
      (isNetwork || !returnNoData),
    refetchInterval: userIsSearching ? false : SAMPLES_REFETCH_INTERVAL,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  const { data: urlsInAnalysisStateData } = useQuery({
    queryKey: advancedSearchKeys.fetchListOfUrlsInAnalysisStatus,
    queryFn: api.fetchListOfUrlsInAnalysisStatus,
    refetchInterval: FETCH_URLS_IN_ANALYZING_STATE_REFETCH_INTERVAL
  });

  const abortQuery = useCallback(() => {
    showCancelRequestNotification();
    abortControllerRef.current?.abort("Aborting previous call.");
    queryClient.invalidateQueries(networkQueryKey as InvalidateQueryFilters);
  }, [networkQueryKey, queryClient]);

  useEffect(() => {
    if (
      isError &&
      [STATUS_CODES.FORBIDDEN, STATUS_CODES.UNAUTHORIZED].includes(
        networkError.status
      )
    ) {
      showCloudUnreachableOrNoAccessNotifications(null);
      return;
    }
  }, [networkError, isError]);

  const { returnData, totalCount } = useMemo(() => {
    if (returnNoData) return { returnData: [], totalCount: 0 };

    const networkDataEntries = networkData?.entries?.map(item => {
      const isAnalyzing = urlsInAnalysisStateData?.data.urls?.includes(
        item.requested_network_location
      );
      return {
        ...item,
        classification: isAnalyzing
          ? AnalysisStatus.PROCESSING
          : item.classification,
        threat_level: isAnalyzing ? undefined : item.threat_level
      };
    });

    const data = sortNetworkReferences(
      (networkDataEntries ?? [])
        .filter(item => filterByClassification(item, classification))
        .filter(item => filterByResourceType(item, type))
        .filter(item => filterByDate(item, timeType, periodType)) ?? [],
      sort
    );

    return {
      returnData: data.slice(
        (page - 1) * pageSize,
        pageSize + (page - 1) * pageSize
      ),
      totalCount: data.length ?? 0
    };
  }, [
    classification,
    networkData?.entries,
    page,
    pageSize,
    periodType,
    returnNoData,
    sort,
    timeType,
    type,
    urlsInAnalysisStateData?.data.urls
  ]);

  const invalidNetworkData = useMemo(
    () =>
      returnNoData
        ? []
        : [
            ...tooLongNetworkValues,
            ...(networkData?.invalid_network_locations ?? [])
          ],
    [networkData?.invalid_network_locations, returnNoData, tooLongNetworkValues]
  );

  const networkIsLoading = useMemo(
    () => isValidating || isLoading,
    [isLoading, isValidating]
  );

  const shouldShow = useMemo(
    () =>
      (!isError || networkError.status === STATUS_CODES.NOT_CHANGED) &&
      !validationError &&
      !isCloudDisabled,
    [isError, networkError?.status, validationError, isCloudDisabled]
  );

  const errorMessage = useMemo(
    () =>
      isError && networkError?.status !== STATUS_CODES.NOT_CHANGED
        ? getSearchError(
            networkError,
            networkData?.entries,
            validationError,
            validationData?.err_message
          )
        : undefined,
    [
      isError,
      networkData?.entries,
      networkError,
      validationData?.err_message,
      validationError
    ]
  );

  return {
    data: returnData,
    isFetching: networkIsLoading,
    totalCount,
    show: shouldShow,
    invalidNetworkData,
    abortQuery,
    nonNTIKeywords,
    isNetworkOnly,
    noKeywordsFound: returnNoData,
    error: errorMessage
  };
};

export default useNetworkSearch;
